import { useId } from 'react';
import { cx } from '../internal/cx';
import { Icon } from './Icon';
import { Input, type InputProps } from './Input';

/** Props of {@link TextField}. */
export interface TextFieldProps extends Omit<InputProps, 'invalid' | 'aria-describedby'> {
  /** Visible label, in the reader's language. */
  label: string;
  /** Help text under the field, e.g. the expected format. */
  hint?: string;
  /** Validation message. Its presence marks the field invalid. */
  error?: string;
  /** Keeps the label for assistive technology but takes it out of the layout. */
  hideLabel?: boolean;
  /** Extra utility classes for the wrapper; the field box keeps its own. */
  wrapperClassName?: string;
}

/**
 * A labelled text field: label, optional hint, optional error, all wired to the input with `useId`
 * so the label is clickable and the messages are announced with the field (WCAG 2.2 AA, 1.3.1 and
 * 3.3.2). The error replaces nothing — the hint stays visible, so the reader keeps the format rule.
 *
 * @example
 * <TextField
 *   label={t('signIn.email')}
 *   type="email"
 *   hint={t('signIn.emailHint')}
 *   error={emailError}
 *   value={email}
 *   onChange={(event) => { setEmail(event.target.value); }}
 * />
 */
export function TextField({
  label,
  hint,
  error,
  hideLabel = false,
  id: idProp,
  className,
  wrapperClassName,
  ...rest
}: TextFieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = cx(hint !== undefined && hintId, error !== undefined && errorId);

  return (
    <div className={cx('flex flex-col gap-(--ui-stack-gap)', wrapperClassName)}>
      <label htmlFor={id} className={cx('text-subhead-strong text-text', hideLabel && 'sr-only')}>
        {label}
      </label>
      <Input
        id={id}
        invalid={error !== undefined}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        className={className}
        {...rest}
      />
      {hint !== undefined && (
        <p id={hintId} className="text-caption text-text-secondary">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} className="flex items-center gap-1 text-caption text-(--ui-danger-strong)">
          <Icon name="error" size="sm" />
          {error}
        </p>
      )}
    </div>
  );
}
